import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';

import { User, UserType } from '../models/Auth';
import { IOAuthConfig, IUserInfo as IUserInfo } from '../models/Auth';

import { environment as ENV } from '../../environments/environment';

const keys = {
    consent: 'consent',
    language: 'language',
    sessionId: 'sid',
    sessionEnd: 'session.end',
    sessionUpdate: 'session.update',
    obligationCheck: 'obligations_check'
};

@Injectable({ providedIn: "root" })
export class AuthService {
    constructor(private http: HttpClient) {
        this.user = this.userSubject.asObservable();
        this.setupListeners();
    }

    readonly user: Observable<User>;

    get apiUrl(): string { return `${ENV.apiUrl}/auth` }

    get currentUser(): User | null {
        return this.userSubject.getValue();
    }

    get sessionId(): string { return this._sessionId; }

    get isAuthenticated(): boolean {
        return !!this.currentUser;
    }

    private _sessionId: string;
    private userSubject = new BehaviorSubject<User>(null);

    private set sessionId(value: string) { this._sessionId = value; }

    loginByToken(payload: {
        user: string,
        time: string,
        name: string,
        hash: string,
        area: string,
        lang: string
    }): Promise<{ user: User, startUrl?: string }> {
          return new Promise((resolve, reject) => {
            this.http.post(`${this.apiUrl}/token`, payload).subscribe((data: { sid: string, user: IUserInfo, redirectUrl?: string }) => {
                this.performAuthentication({
                    sid: data?.sid,
                    user: data?.user,
                    updateAllSessions: true
                }).then(user => resolve({ user, startUrl: data.redirectUrl }));
            }, err => reject(err));
        });
    }

    loginByCrmToken(payload: {
        custId: string,
        custIdType: string,
        adLogin: string,
        time: string,
        token: string,
        lang: string
    }): Promise<{ user: User, startUrl?: string }> {
        return new Promise((resolve, reject) => {
            this.http.post(`${this.apiUrl}/crmToken`, payload).subscribe((data: { sid: string, user: IUserInfo, redirectUrl?: string }) => {
                return this.performAuthentication({
                    sid: data?.sid,
                    user: data?.user,
                    updateAllSessions: true
                }).then(user => resolve({ user, startUrl: data.redirectUrl }));
            }, err => reject(err));
        });
    }

    /**
     * Initiate the login process.
     * @param returnTo URL to redirect to after successfull login
     */
    login(returnTo?: string) {
        let redirecturl = '';

        if (returnTo) {
            redirecturl = '?redirecturl=' + encodeURIComponent(returnTo);
        }

        location.assign(ENV.adfsApiRoute + redirecturl);
    }

    /**
     * Logout current user.
     * @param returnTo URL to redirect to after successfull logout
     */
    logout(returnTo?: string) {
        const url = `${this.apiUrl}/logout`;

        if (!returnTo) {
            returnTo = `${location.origin}/${sessionStorage.getItem(keys.language) || 'lv'}/${ENV.startRoute}?nologin=true`;
        }

        this.http.post(url, null).toPromise().then(() => {
            this.endSession({ emitUser: false, endAllSessions: true });
            const signoutUrl = `${ENV.adfsApiRoute}/signout?returnTo=${encodeURIComponent(returnTo)}`;
            location.assign(signoutUrl);
        });
    }

    /**
     * Get OAuth configuration parameters
     */
    getOAuthConfiguration(): Observable<IOAuthConfig> {
        const url = `${this.apiUrl}/oauthConfig`;
        return this.http.get<IOAuthConfig>(url);
    }

    /**
     * Authenticate a user using browser available data.
     */
    authenticate(): Promise<{ user: User, startUrl?: string }> {
        // On successfull login the app receives a session ID and the cookie.
        const url = new URL(document.URL);
        let sid = url.searchParams.get('sid');

        let isNew = false;

        if (sid) {
            // after successfull login a new session ID is generated
            isNew = true;
        } else {
            // otherwise, get local or shared session ID
            sid = this.sessionId || localStorage.getItem(keys.sessionId);

            if (!sid) {
                // no session means we should force the authentication
                isNew = true;
            }
        }

        if (isNew) {
            localStorage.removeItem(keys.obligationCheck);
        }

        return new Promise(resolve => {
            this.performAuthentication({
                sid,
                force: isNew,
                updateAllSessions: isNew
            }).then(user => {
                resolve({ user, startUrl: ENV.startRoute });
            });
        });
    }

    /**
     * Set a user using provided user data.
     * @param user
     */
    setUser(user: IUserInfo) {
        const data = this.mapUser(user);

        // In case user updated directly (e.g., impersonation) we need to force update all other sessions.
        this.setSession(data, { updateAllSessions: true });
    }

    private setSession(user: User, options?: { emitUser?: boolean, updateAllSessions?: boolean }) {
        const opts = { emitUser: true, updateAllSessions: false };
        Object.assign(opts, options);

        if (user) {
            sessionStorage.removeItem(keys.consent);

            // share the session ID between browser tabs
            localStorage.setItem(keys.sessionId, this.sessionId);
        } else {
            sessionStorage.removeItem(keys.consent);

            // stop sharing the session ID
            localStorage.removeItem(keys.sessionId);
        }

        if (opts.emitUser) {
            this.userSubject.next(user);
        }

        if (opts.updateAllSessions) {
            this.fireEvent(keys.sessionUpdate, '1');
        }
    }

    private endSession(options?: { emitUser?: boolean, endAllSessions?: boolean }) {
        const opts = { emitUser: true, endAllSessions: true };
        Object.assign(opts, options);

        this.setSession(null, { emitUser: opts.emitUser });

        if (opts.endAllSessions) {
            this.fireEvent(keys.sessionEnd, '1');
        }
    }

    private mapUser(data: IUserInfo): User {
        const user = new User();

        user.id = data.Id;
        user.email = data.Email;
        user.firstName = data.FirstName;
        user.lastName = data.LastName;
        user.roles = data.Roles || [];
        user.rights = data.Rights || [];
        user.username = data.UserName;
        user.type = data.UserType as UserType;
        user.personDetailsId = data.PersonDetailsId;

        if (data.Impersonator) {
            user.impersonator = this.mapUser(data.Impersonator);
        }

        return user;
    }

    private setupListeners() {
        window.addEventListener('storage', event => {
            switch (event.key) {
                case keys.sessionUpdate:
                    if (event.newValue == '1') {
                        const sid = localStorage.getItem(keys.sessionId);
                        this.performAuthentication({ sid, force: true });
                    }
                    break;

                case keys.sessionEnd:
                    if (event.newValue == '1') {
                        this.endSession({ emitUser: true, endAllSessions: false });
                    }
                    break;
            }
        });
    }

    private fireEvent(name: string, value: string) {
        localStorage.setItem(name, value);
        localStorage.removeItem(name);
    }

    private performAuthentication(options: {
        /**
         * Session ID
         */
        sid: string,
        /**
         * Authenticated used data (to avoid extra request)
         */
        user?: IUserInfo,
        /**
         * Force the authentication even if it's the same session (session ID did not change)
         */
        force?: boolean,
        /**
         * Update all sibling browser sessions
         */
        updateAllSessions?: boolean
    }): Promise<User> {
        const sid = options.sid;

        if (!sid || sid === 'null' || sid === 'undefined') {
            return Promise.resolve(null);
        }

        const opts = { force: false, updateAllSessions: false };
        Object.assign(opts, options);

        if (sid == this.sessionId && !opts.force) {
            return Promise.resolve(this.currentUser);
        }

        // session ID must be set before any subsequent requests, otherwise the interceptor will use an old value
        this.sessionId = sid;

        return new Promise(resolve => {
            const proceed = (data: IUserInfo) => {
                if (data) {
                    const user = this.mapUser(data);
                    this.setSession(user, { updateAllSessions: opts.updateAllSessions });
                    resolve(user);
                } else {
                    this.endSession({ emitUser: true, endAllSessions: true });
                    resolve(null);
                }
            };

            if (options.user) {
                proceed(options.user);
            } else {
                const url = `${this.apiUrl}/user`;

                this.http.get(url).subscribe((data: IUserInfo) => {
                    proceed(data);   
                }, err => {
                    // end faulty session
                    this.endSession({ emitUser: true, endAllSessions: true });
                    resolve(null);
                });
            }
        });
    }
}
